import ApiService from '@/common/api.service'
import * as utils from '@/common/utils'

const module = 'units/commandsModule'

/**
 * Returns all properties and their default state.
 *
 * @return Object
 */
function properties () {
  return {
    id: null,
    description: '',
    unit_type_id: null,
    unit_type_code: '',
    unit_type_name: '',
    unit_model_id: null,
    unit_model_code: '',
    unit_model_name: '',
    sending_method: 'sms',
    command_data: []
  }
}

/**
 * Returns a copy of the default state.
 *
 * @return Object
 */
function defaults () {
  return {
    ...properties()
  }
}

function formDefaults () {
  return {
    unit_types: [],
    unit_models: [],
    send_methods: []
  }
}

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param unit_command      Object
 *
 * @return Object
 */
function transformCreate (unit_command) {
  const data = unit_command.command_data.map((item) => {
    return {
      sending_method: item.sending_method,
      data: { ...item.data }
    }
  })

  // Some commands require spaces in front of or behind the text that needs to be transmitted.
  // For example, Teltonika devices typically have a command structure like this:
  // "user pass command". If the user and/or password is not required, a space character
  // should be provided instead, e.g.: "  command"
  // We need to replace spaces in the command with the HTML entity &nbsp;
  // This is so that the API doesn't trim the spaces away
  data.forEach(item => {
    item.data.text = item.data.text.replace(/^ +| +$/g, match => '&nbsp;'.repeat(match.length))
  })

  return {
    id: unit_command.id,
    description: unit_command.description,
    unit_type_id: unit_command.unit_type_id,
    unit_model_id: unit_command.unit_model_id,
    command_data: data
  }
}

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param unit_command  Object
 *
 * @return Object
 */
function transformUpdate (unit_command) {
  const data = unit_command.command_data.map((item) => {
    return {
      sending_method: item.sending_method,
      data: { ...item.data }
    }
  })

  // Some commands require spaces in front of or behind the text that needs to be transmitted.
  // For example, Teltonika devices typically have a command structure like this:
  // "user pass command". If the user and/or password is not required, a space character
  // should be provided instead, e.g.: "  command"
  // We need to replace spaces in the command with the HTML entity &nbsp;
  // This is so that the API doesn't trim the spaces away
  data.forEach(item => {
    item.data.text = item.data.text.replace(/^ +| +$/g, match => '&nbsp;'.repeat(match.length))
  })

  return {
    id: unit_command.id,
    description: unit_command.description,
    unit_type_id: unit_command.unit_type_id,
    unit_model_id: unit_command.unit_model_id,
    command_data: data
  }
}

/**
 * The module's state (VueX store object)
 */
export const state = {
  data: Object.assign({}, defaults()),
  form: Object.assign({}, formDefaults()),
  original: properties(),
  isModified: false,
  isLoading: false
}

export const getters = {
  /**
   * Returns the full state object
   *
   * @return Object
   */
  get (state) {
    return state.data
  },

  /**
   * Returns the form's state object
   *
   * @return Object
   */
  getForm (state) {
    return state.form
  },

  /**
   * Returns true if the primary data have been modified
   * since it was originally loaded.
   *
   * @return Boolean
   */
  isModified (state) {
    return state.isModified
  },

  /**
   * Returns true if the data for the module is still being loaded.
   *
   * @return Boolean
   */
  isLoading (state) {
    return state.isLoading
  }

}

export const actions = {
  /**
   * Request a record from the API.
   *
   * @param context Object
   * @param id      Numeric
   *
   * @return Promise
   */
  fetch (context, id) {
    return ApiService
      .get(`unit/commands/${id}`)
      .then((response) => {
        context.commit('set', response.data.data)
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * Request "create" form data from the API.
   *
   * @param context Object
   *
   * @return Promise
   */
  fetchFormCreate (context) {
    return ApiService
      .get(`unit/commands/create`)
      .then((response) => {
        context.commit('set', defaults())
        context.commit('setFormData', response.data.data)
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * Request a record and "edit" form data from the API.
   *
   * @param context Object
   * @param id      Numeric
   *
   * @return Promise
   */
  fetchFormEdit (context, id) {
    return ApiService
      .get(`unit/commands/${id}/edit/`)
      .then((response) => {
        context.commit('set', response.data.data)
        context.commit('setFormData', response.data.data)
        context.commit('setOriginal', response.data.data)
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * POST (create) new record through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @return Promise
   */
  post (context) {
    const unit_command = context.getters['get']

    return ApiService
      .post(`unit/commands`, transformCreate(unit_command))
      .then((response) => {
        context.commit('set', defaults())
        context.commit('setIsModified', false)
        utils.addRecentHistory(module, response.data.data)
        return response
      })
  },

  /**
   * PUT (update) existing record through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @return Promise
   */
  put (context) {
    const unit_command = context.getters['get']
    return ApiService
      .put(`unit/commands/${unit_command.id}`, transformUpdate(unit_command))
      .then((response) => {
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * DELETE existing record through the API.
   *
   * @param context Object
   *
   * @return Promise
   */
  delete (context) {
    const id = context.getters['get'].id
    return ApiService
      .delete(`unit/commands/${id}`)
      .then((response) => {
        return response
      })
  },

  /**
   * Gets the unit models for a given unit type
   *
   * @param context
   * @param params
   * @returns Promise
   */
  getUnitModels (context, params) {
    return ApiService
      .get(`units/models/by-type-id/${params.unit_type_id}`, params)
      .then((response) => {
        context.commit('setUnitModelsData', response.data.data)
        return response
      })
  },

  /**
   * Queries (e.g. find) multiple records from the API.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  query (context, params) {
    return ApiService
      .get(`unit/commands`, params)
      .then((response) => {
        return response
      })
  }
}

export const mutations = {
  /**
   * Set all the data properties according to the provided data.
   *
   * @param state Object
   * @param data  Object
   */
  set (state, data) {
    state.data.sending_method = 'sms'
    state.data.command_data = data.command_data
    state.data.description = data.description
    state.data.unit_type_id = data.unit_type_id
    state.data.unit_type_code = data.unit_type_code
    state.data.unit_type_name = data.unit_type_name
    state.data.unit_model_id = data.unit_model_id
    state.data.unit_model_code = data.unit_model_code
    state.data.unit_model_name = data.unit_model_name
    state.data.id = data.id
  },

  setFormData (state, data) {
    state.form = {
      unit_types: data.unit_types,
      unit_models: data.unit_models,
      send_methods: data.send_methods
    }
  },

  setUnitModelsData (state, data) {
    state.form.unit_models = data
  },

  restUnitModels (state) {
    state.data.unit_model_id = null
    state.form.unit_models = []
  },

  /**
   * Sets the original primary data.
   *
   * @param state Object
   * @param data  Object
   */
  setOriginal (state, data) {
    state.original = {
      description: data.description,
      unit_type_id: data.unit_type_id,
      unit_type_code: data.unit_type_code,
      unit_type_name: data.unit_type_name,
      unit_model_id: data.unit_model_id,
      unit_model_code: data.unit_model_code,
      unit_model_name: data.unit_model_name,
      command_data: data.command_data
    }
  },

  /**
   * Sets the "modified" status to true or false
   *
   * @param state Object
   * @param isModified Boolean
   */
  setIsModified (state, isModified) {
    state.isModified = isModified
  },

  /**
   * Sets the "isLoading" status to true or false
   *
   * @param state Object
   * @param isLoading Boolean
   */
  setIsLoading (state, isLoading) {
    state.isLoading = isLoading
  },

  /**
   * Sets a specific property and value
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value String
   */
  setProp (state, { name, value }) {
    state.isModified = true
    utils.mutateProp(state.data, name, value)
  },

  /**
   * Pushes a new element to an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value mixed  The variable to push onto the array.
   */
  pushProp (state, { name, value }) {
    state.isModified = true
    state.data[name].push(value)
  },

  /**
   * removes an element from an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param index Number The index of the element to remove
   */
  pullProp (state, { name, index }) {
    state.isModified = true
    utils.pullProp(state.data, name, index)
  },

  /**
   * Sets the values for multiple properties.
   *
   * @param state      Object
   * @param properties Array of objects in the form { name: 'propName', value: 'xyz' }
   */
  setProps (state, properties) {
    state.isModified = true
    properties.forEach(({ name, value }) => {
      utils.mutateProp(state.data, name, value)
    })
  },

  /**
   * removes an element from an array of a specific property
   *
   * @param state   Object
   * @param name    String Name of property
   * @param index   Number The index of the element to remove
   */
  removeFromCommandData (state, { name, index }) {
    state.isModified = true
    state.data.command_data.splice(index, 1)
  },

  /**
   * removes an element from an array of a specific property
   * and replaced it with a new element.
   *
   * @param state   Object
   * @param name    String Name of property
   * @param index   Number The index of the element to remove
   * @param replacement New element to replace the old
   */
  spliceProp (state, { name, index, replacement }) {
    state.isModified = true
    state.data[name].splice(index, 1, replacement)
  },

  /**
   * Resets the state.
   *
   * @param state Object
   */
  reset (state) {
    state.data = defaults()
    state.original = properties()
    state.isModified = false
    state.isLoading = false
  },

  /**
   * Resets the form's state.
   *
   * @param state Object
   */
  resetForm (state) {
    state.form = formDefaults()
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/units/commands/commandsModule.js